import type {PaletteColorOptions, PaletteOptions} from '@material-ui/core/styles/createPalette';

/*
 * Color Palette Guide =
 *
 * The number indicates a relative lightness/darkness for each color gradient.
 * The colors transition from highest value to lowest value as the numbers increase.
 * The hue is generally constant in each range.
 *
 * 020 - 200 will typically be reserved for backgrounds
 */

declare module '@material-ui/core' {
    export interface Color {
        20: string;
    }
}

declare module '@material-ui/core/styles/createPalette' {
    export interface PaletteOptions {
        samaritan: PaletteColorOptions;
        // Categories
        indigo: PaletteColorOptions;
        sienna: PaletteColorOptions;
        lilac: PaletteColorOptions;
        olive: PaletteColorOptions;
        port: PaletteColorOptions;
    }

    export interface Palette {
        samaritan: PaletteColor;
        // Categories
        indigo: PaletteColor;
        sienna: PaletteColor;
        lilac: PaletteColor;
        olive: PaletteColor;
        port: PaletteColor;
    }
}

/**
 * This should only be imported for creating a theme object. Access to the properties should be done through the theme
 * object provided by a ThemeProvider.
 */
export const PaletteLight: PaletteOptions = {
    type: 'light',
    text: {
        // Body
        primary: '#5B5B5B',
        // Dark
        secondary: '#333333',
        // Muted
        disabled: '#B3B3B3',
        hint: '#767676',
    },
    background: {
        default: '#FAFAFA',
        paper: '#FFFFFF',
    },
    divider: '#E0E0E0',
    common: {
        white: '#FFFFFF',
        black: '#1F1F1F',
    },
    primary: {
        main: '#3294BA',
        contrastText: '#FFFFFF',
        20: '#ECF8FD',
        50: '#ECF8FD',
        100: '#D9F1FA',
        200: '#B2E3F6',
        300: '#79CEEF',
        400: '#3FB9E8',
        500: '#3294BA',
        600: '#266F8B',
        700: '#205D74',
        800: '#133746',
    },
    // Accent 1
    secondary: {
        main: '#31B395',
        contrastText: '#FFFFFF',
        20: '#EDFDF9',
        50: '#EDFDF9',
        100: '#C5F8EC',
        200: '#93F2DC',
        300: '#3BDBB6',
        400: '#36C8A7',
        500: '#31B395',
        600: '#2A9A80',
        700: '#217965',
        800: '#13473B',
    },
    //Critical
    error: {
        main: '#DC3449',
        contrastText: '#FFFFFF',
        20: '#FEF5F7',
        50: '#FEF5F7',
        100: '#FDDEE2',
        200: '#FDBAC2',
        300: '#FD96A4',
        400: '#FC5A6F',
        500: '#DC3449',
        600: '#C32238',
        700: '#9C0D1D',
        800: '#6B0613',
    },
    warning: {
        main: '#C45402',
        contrastText: '#FFFFFF',
        20: '#FEF7F1',
        50: '#FEF7F1',
        100: '#FCDDC5',
        200: '#F8C096',
        300: '#F4A162',
        400: '#E97116',
        500: '#C45402',
        600: '#AD4B00',
        700: '#803700',
        800: '#572600',
    },
    // Accent 2
    info: {
        main: '#BC9D33',
        contrastText: '#FFFFFF',
        20: '#FDF9EE',
        50: '#FDF9EE',
        100: '#F8EDC8',
        200: '#F4E09F',
        300: '#EED171',
        400: '#E7C03F',
        500: '#BC9D33',
        600: '#A2862C',
        700: '#806A23',
        800: '#4B3E14',
    },
    // Correct
    success: {
        main: '#14892F',
        contrastText: '#FFFFFF',
        20: '#ECFEF1',
        50: '#ECFEF1',
        100: '#CCEED2',
        200: '#A0D9AD',
        300: '#77C589',
        400: '#37A851',
        500: '#14892F',
        600: '#13772A',
        700: '#025A15',
        800: '#003D0E',
    },
    samaritan: {
        main: '#AF50AF',
        contrastText: '#FFFFFF',
        20: '#FFF5FF',
        50: '#FFEBFF',
        100: '#FBDBFB',
        200: '#EFBDEF',
        300: '#E29CE2',
        400: '#CC75CC',
        500: '#AF50AF',
        600: '#9A429A',
        700: '#773177',
        800: '#521E52',
    },
    // Categories
    indigo: {
        main: '#5F73BA',
        contrastText: '#FFFFFF',
        20: '#F5F7FF',
        50: '#F0F3FF',
        100: '#E0E6FA',
        200: '#C1CBEC',
        300: '#A5B2E4',
        400: '#8091D1',
        500: '#5F73BA',
        600: '#5062A5',
        700: '#384A85',
        800: '#24315B',
    },
    lilac: {
        main: '#8969A5',
        contrastText: '#FFFFFF',
        20: '#F8EEFF',
        50: '#F8EEFF',
        100: '#EFDFFC',
        200: '#DBC6EC',
        300: '#C3AADA',
        400: '#A787C0',
        500: '#8969A5',
        600: '#795993',
        700: '#5B4370',
        800: '#3E2B4F',
    },
    olive: {
        main: '#5E803C',
        contrastText: '#FFFFFF',
        20: '#F2FDE8',
        50: '#E9F7D9',
        100: '#D9EBC7',
        200: '#BBD5A2',
        300: '#9EBE7E',
        400: '#7D9E5C',
        500: '#5E803C',
        600: '#526F34',
        700: '#3C5423',
        800: '#23390E',
    },
    port: {
        main: '#AF5A85',
        contrastText: '#FFFFFF',
        20: '#FEF5FA',
        50: '#FEEFF8',
        100: '#F5E0EC',
        200: '#ECC1D4',
        300: '#E0A3C1',
        400: '#C97CA3',
        500: '#AF5A85',
        600: '#9B4B73',
        700: '#7A3658',
        800: '#53223A',
    },
    sienna: {
        main: '#BC583F',
        contrastText: '#FFFFFF',
        20: '#FFF3F0',
        50: '#FFEEEA',
        100: '#FBE1DB',
        200: '#F3C2B6',
        300: '#EAA18F',
        400: '#D87A63',
        500: '#BC583F',
        600: '#A64A33',
        700: '#803624',
        800: '#572414',
    },
    // Neutral
    grey: {
        20: '#F8F9FA',
        50: '#F8F9FA',
        100: '#EBEEEF',
        200: '#DCE2E4',
        300: '#CCD4D8',
        400: '#BBC6CA',
        500: '#A7B5BB',
        600: '#91A3AA',
        700: '#616E74',
        800: '#394144',
    },
};
